'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { useTrustpilot } from '@/ui/providers/trustpilot-provider';
import { getTrustpilotStarsImageUrl } from './trustpilot-stars-image';

/**
 * One-line Trustpilot rating for dense layouts such as the product purchase panel:
 * "[stars] 4.1 · 1,830 reviews", linked to the public profile. Reads the server-resolved rating
 * synchronously like the full widget, and renders nothing while no rating is available so the
 * surrounding slot collapses.
 */
export function TrustpilotCompactRating() {
  const rating = useTrustpilot();
  const t = useTranslations('TrustpilotWidget');

  if (!rating) {
    return null;
  }

  return (
    <a
      href={rating.profileUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="trustpilot-compact-rating"
      className="inline-flex items-center gap-2 text-xs text-neutral-55 no-underline hover:underline"
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- self-hosted brand asset, not optimizable */}
      <img
        src={getTrustpilotStarsImageUrl(rating.stars)}
        alt={t('starRatingAlt', { rating: rating.stars })}
        width={100}
        height={19}
        style={{ height: '16px', width: 'auto' }}
      />
      <span className="whitespace-nowrap">
        {/* Trustpilot shows the score with a dot decimal in every locale. */}
        {rating.trustScore.toFixed(1)} · {rating.reviewsLabel}
      </span>
    </a>
  );
}
